import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { type QuestionnaireDto, questionnaireDtoSchema } from '../schemas/questionnaireSchemas';
import { questionnaireKeys } from './questionnaireKeys';

interface Vars {
  questionnaireId: string;
  name: string;
}

/**
 * Renames a questionnaire without touching its questions.
 *
 * A DEDICATED ROUTE, not `useSaveQuestionnaire`. Save reconciles the whole tree and deletes anything
 * absent from the payload, and the list screen only holds counts — so renaming through Save would
 * have emptied the questionnaire it renamed.
 */
export function useRenameQuestionnaire() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async ({ questionnaireId, name }: Vars): Promise<QuestionnaireDto> => {
      const raw = await apiClient
        .patch(`api/v1/t/${tenantId}/questionnaires/${questionnaireId}/name`, { json: { name } })
        .json();
      return questionnaireDtoSchema.parse(raw);
    },
    onSuccess: (dto) => {
      qc.invalidateQueries({ queryKey: questionnaireKeys.lists() });
      // The builder may be showing this questionnaire — drop its cached detail so the new name is
      // picked up rather than the header keeping the old one until a reload.
      qc.invalidateQueries({ queryKey: questionnaireKeys.detail(dto.id) });
    },
  });
}
